import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from './Button';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function BookingModal({
  isOpen,
  onClose
}: BookingModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    request: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Handle escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';else document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    if (!formData.request.trim()) newErrors.request = 'Please tell us what you need';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    // Simulate network request
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const handleClose = () => {
    onClose();
    // Reset after animation
    setTimeout(() => {
      setIsSuccess(false);
      setFormData({
        name: '',
        phone: '',
        email: '',
        request: ''
      });
      setErrors({});
    }, 300);
  };
  return <AnimatePresence data-id="element-54">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-55">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm" aria-hidden="true" data-id="element-56" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} role="dialog" aria-modal="true" aria-labelledby="modal-title" className="relative w-full max-w-lg bg-white shadow-2xl overflow-hidden" data-id="element-57">
            {/* Header */}
            <div className="bg-brand-purple p-6 text-white text-center relative" data-id="element-58">
              <button onClick={handleClose} className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors" aria-label="Close modal" data-id="element-59">
                <X className="w-6 h-6" data-id="element-60" />
              </button>
              <h2 id="modal-title" className="text-3xl font-serif mb-2 flex items-center justify-center gap-2" data-id="element-61">
                <Sparkles className="w-5 h-5 text-brand-gold" data-id="element-62" />
                Book Your Glamour
                <Sparkles className="w-5 h-5 text-brand-gold" data-id="element-63" />
              </h2>
              <p className="text-brand-light/90 font-light" data-id="element-64">
                Ready for the red carpet? Let's get you scheduled.
              </p>
            </div>

            <div className="p-6 sm:p-8" data-id="element-65">
              {isSuccess ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-center py-8" data-id="element-66">
                  <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-67">
                    <CheckCircle2 className="w-10 h-10 text-brand-purple" data-id="element-68" />
                  </div>
                  <h3 className="text-2xl font-serif text-brand-dark mb-4" data-id="element-69">
                    Fabulous, {formData.name.split(' ')[0]}!
                  </h3>
                  <p className="text-gray-600 mb-8" data-id="element-70">
                    Your request has been received. Shazz or her team will reach
                    out to you shortly to confirm your appointment.
                  </p>
                  <Button onClick={handleClose} className="w-full" data-id="element-71">
                    Close
                  </Button>
                </motion.div> : <>
                  {/* Direct Call Option */}
                  <div className="mb-8 p-4 bg-brand-light/50 border border-brand-purple/10 text-center" data-id="element-72">
                    <p className="text-sm text-gray-600 mb-2 uppercase tracking-wider font-semibold" data-id="element-73">
                      Need immediate assistance?
                    </p>
                    <a href="tel:+16463974299" className="inline-flex items-center gap-2 text-xl font-serif text-brand-purple hover:text-brand-gold transition-colors" data-id="element-74">
                      <Phone className="w-5 h-5" data-id="element-75" />
                      +1 (646) 397-4299
                    </a>
                  </div>

                  <div className="flex items-center gap-4 mb-6" data-id="element-76">
                    <div className="h-px bg-gray-200 flex-1" data-id="element-77"></div>
                    <span className="text-sm text-gray-400 uppercase tracking-widest" data-id="element-78">
                      Or Request Online
                    </span>
                    <div className="h-px bg-gray-200 flex-1" data-id="element-79"></div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-80">
                    <div data-id="element-81">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-82">
                        Full Name
                      </label>
                      <input type="text" id="name" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className={`w-full px-4 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-brand-purple focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white`} placeholder="Paris Hilton" data-id="element-83" />
                      {errors.name && <p className="text-red-500 text-xs mt-1" data-id="element-84">
                          {errors.name}
                        </p>}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-85">
                      <div data-id="element-86">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-87">
                          Phone Number
                        </label>
                        <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-brand-purple focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white`} placeholder="(555) 123-4567" data-id="element-88" />
                        {errors.phone && <p className="text-red-500 text-xs mt-1" data-id="element-89">
                            {errors.phone}
                          </p>}
                      </div>
                      <div data-id="element-90">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-91">
                          Email Address
                        </label>
                        <input type="email" id="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-brand-purple focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white`} placeholder="paris@example.com" data-id="element-92" />
                        {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-93">
                            {errors.email}
                          </p>}
                      </div>
                    </div>

                    <div data-id="element-94">
                      <label htmlFor="request" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-95">
                        Hair Request / Event Details
                      </label>
                      <textarea id="request" rows={3} value={formData.request} onChange={e => setFormData({
                  ...formData,
                  request: e.target.value
                })} className={`w-full px-4 py-3 border ${errors.request ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-brand-purple focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white resize-none`} placeholder="Tell us about the look you want or the event you're attending..." data-id="element-96" />
                      {errors.request && <p className="text-red-500 text-xs mt-1" data-id="element-97">
                          {errors.request}
                        </p>}
                    </div>

                    <Button type="submit" className="w-full mt-6" disabled={isSubmitting} data-id="element-98">
                      {isSubmitting ? 'Sending Request...' : 'Request Appointment'}
                    </Button>
                  </form>
                </>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}